import { effect, Signal, useSignalEffect } from '@preact/signals-react';
import { useContext, useEffect, useRef } from 'react';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  AiVisualEditMessage,
  NewElementMessage,
} from '@/features/editor/context/use-device-preview';
import {
  AiComponentTab,
  VisualGroupInfo,
} from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { ContextualAttachment } from '@/features/ai-agents/shared/models';

export interface LifecycleCallbacks {
  removeByGroupName: (group: string, selector: string) => void;
  removeAllByGroupName: (group: string, selector: string) => void;
  selectTab: (tab: AiComponentTab) => void;
}

export function useComponentLifecycle(
  newChange: Signal<EditorDeclarativeBlock>,
  selectedTab: Signal<AiComponentTab>,
  isFullScreen: Signal<boolean>,
  highlightedVisualGroup: Signal<VisualGroupInfo | undefined>,
  contextualAttachments: Signal<ContextualAttachment[]>,
  sessionData: Signal<Record<string, any>>,
  callbacks: LifecycleCallbacks,
) {
  const {
    inspectorNav: { gotoAppCatalog },
    experienceState: {
      upsertEditorChange,
      currentExperience,
      currentVariantIdx,
    },
    devicePreview: {
      editComponent,
      editorState: { device, installedFonts, mostPopularFont },
    },
    deviceNavigation: { previewUrl, urlToStorePage },
  } = useContext(EditorContext);

  const mountsCount = useRef(0);

  // Update session data when context changes
  effect(() => {
    const pageType = urlToStorePage(previewUrl);
    const data = {
      device,
      most_popular_font: mostPopularFont,
      installed_fonts: installedFonts,
      page_url: previewUrl,
      page_type: pageType.kind,
      page_title: pageType.caption,
      section_id: newChange.value.editorId,
      experience_id: currentExperience?.id,
      experience_variant_id:
        currentExperience?.variants?.[currentVariantIdx]?.id,
    };
    contextualAttachments?.value?.forEach((attachment) => {
      data[`attachment_${attachment.id}`] = attachment.payload;
    });
    sessionData.value = data;
  });

  // Auto-disable fullscreen when not in CODE tab
  effect(() => {
    if (isFullScreen.value && selectedTab.value !== AiComponentTab.CODE) {
      isFullScreen.value = false;
    }
  });

  // Mount component on first render
  effect(() => {
    if (mountsCount.current === 0) {
      editComponent(newChange.value.editorSelector);
      mountsCount.current += 1;
    }
  });

  // Visual edit handler
  const visualEditHandler = (ev: any) => {
    const detail = ev?.detail as AiVisualEditMessage;
    if (detail.subAction === `scroll`) {
      highlightedVisualGroup.value = {
        group: detail.group,
        selector: detail.selector,
      };
      if (selectedTab.value !== AiComponentTab.EDIT) {
        callbacks.selectTab(AiComponentTab.EDIT);
      }
      return;
    }
    if (detail.subAction === `remove`) {
      callbacks.removeByGroupName(detail.group, detail.selector);
    }
    if (detail.subAction === `removeAll`) {
      callbacks.removeAllByGroupName(detail.group, detail.selector);
    }
    if ([`addAfter`, `addBefore`].includes(detail.subAction)) {
      upsertEditorChange(newChange.value);
      gotoAppCatalog({
        action: `create`,
        kind: detail.subAction.replace(`add`, `append`),
        selector: detail.selector,
      } as NewElementMessage);
    }
  };

  // Sync selected tab to window global
  useSignalEffect(() => {
    window[`vslyAiEditorSelectedTab`] = selectedTab.value.toLowerCase();
  });

  // Setup event listeners
  useEffect(() => {
    window.addEventListener(`ai-visual-edit`, visualEditHandler);
    return () => {
      window.removeEventListener(`ai-visual-edit`, visualEditHandler);
      window[`vslyAiEditorSelectedTab`] = undefined;
    };
  }, []);
}
